/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Chat Input Bar - Text input with send button, upload, and settings
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Paperclip, Loader2 } from 'lucide-react';
import { Button } from '@/components/v2/ui';
import { ChatImageSettings } from './ChatImageSettings';

interface Props {
  onSend: (text: string, settings: { aspect_ratio?: string; resolution?: string }) => void;
  onUpload: (files: File[]) => void;
  isLoading: boolean;
  isUploading: boolean;
  disabled: boolean;
  canUpload: boolean;
  suggestedAspectRatio?: string | null;
}

export function ChatInputBar({ onSend, onUpload, isLoading, isUploading, disabled, canUpload, suggestedAspectRatio }: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [aspectRatio, setAspectRatio] = useState<string>('1:1');

   
  useEffect(() => {
    if (suggestedAspectRatio) setAspectRatio(suggestedAspectRatio);
  }, [suggestedAspectRatio]);
  const [resolution, setResolution] = useState<string>('1K');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed, { aspect_ratio: aspectRatio, resolution });
    setText('');
    textareaRef.current?.focus();
  }, [text, isLoading, onSend, aspectRatio, resolution]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) onUpload(files);
    e.target.value = '';
  }, [onUpload]);

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-800 p-3">
      <ChatImageSettings
        aspectRatio={aspectRatio}
        resolution={resolution}
        onAspectRatioChange={setAspectRatio}
        onResolutionChange={setResolution}
      />
      <div className="flex items-end gap-2 mt-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canUpload || isUploading || disabled}
          title={canUpload ? t('chatImage.uploadImage') : t('chatImage.maxImages')}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('chatImage.inputPlaceholder')}
          disabled={disabled || isLoading}
          rows={1}
          className="flex-1 resize-none rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
        />
        <Button
          variant="primary"
          size="sm"
          onClick={handleSend}
          disabled={!text.trim() || isLoading || disabled}
          loading={isLoading}
          iconLeft={<Send className="h-4 w-4" />}
        >
          {t('chatImage.send')}
        </Button>
      </div>
    </div>
  );
}
